export const packageOptions = ['Basic', 'Standard', 'Premium'];

/** @deprecated Prefix titles are no longer collected on member forms. */
export const prefixOptions = [];

export const trainerOptions = ['Unassigned'];

const NAME_TITLE_RE = /^(mr|mrs|ms|miss|dr|mx)\.?$/i;

/** Strip Mr/Mrs/Ms/Dr/Mx titles from a stored full name for display. */
export function stripMemberNameTitle(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '';
  while (parts.length && NAME_TITLE_RE.test(parts[0])) {
    parts.shift();
  }
  return parts.join(' ').trim();
}

export function getMemberInitials(nameOrForm) {
  if (typeof nameOrForm === 'object' && nameOrForm !== null) {
    const first = nameOrForm.firstName || stripMemberNameTitle(nameOrForm.name) || 'M';
    const last = nameOrForm.lastName || '';
    if (last) return `${first[0]}${last[0]}`.toUpperCase();
    return first.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
  }
  const cleaned = stripMemberNameTitle(nameOrForm) || String(nameOrForm || '');
  return cleaned.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
}

export function buildMemberName(form) {
  return [form.firstName, form.lastName].filter(Boolean).join(' ').trim();
}

export function buildMemberAddress(form) {
  return [form.addressLine1, form.addressLine2, form.city, form.state, form.country, form.zipCode]
    .filter(Boolean)
    .join(', ');
}

export function memberToFormData(member) {
  if (member.firstName) {
    return {
      photo: member.photo,
      photoPreview: member.photoPreview || null,
      firstName: member.firstName,
      lastName: member.lastName || '',
      mobile: member.mobile || member.phone || '',
      alternateMobile: member.alternateMobile || '',
      landline: member.landline || '',
      cnic: member.cnic || '',
      email: member.email || '',
      dob: member.dob || '',
      gender: member.gender || 'male',
      addressLine1: member.addressLine1 || '',
      addressLine2: member.addressLine2 || '',
      city: member.city || '',
      state: member.state || '',
      country: member.country || 'Pakistan',
      zipCode: member.zipCode || '',
      package: member.package,
      trainer: member.trainer,
      branch: member.branch || '',
      joiningDate: member.joiningDate,
      createdAt: member.joiningDate || member.createdAt || '',
      emergencyContact: member.emergencyContact || '',
      status: member.status || 'active',
    };
  }

  const nameParts = stripMemberNameTitle(member.name || '').split(/\s+/).filter(Boolean);
  const firstName = nameParts[0] || '';
  const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';

  return {
    photo: member.photo,
    photoPreview: member.photoPreview || null,
    firstName,
    lastName,
    mobile: member.mobile || member.phone || '',
    alternateMobile: member.alternateMobile || '',
    landline: member.landline || '',
    cnic: member.cnic || '',
    email: member.email || '',
    dob: member.dob || '',
    gender: member.gender || 'male',
    addressLine1: member.addressLine1 || member.address || '',
    addressLine2: member.addressLine2 || '',
    city: member.city || '',
    state: member.state || '',
    country: member.country || 'Pakistan',
    zipCode: member.zipCode || '',
    package: member.package,
    trainer: member.trainer,
    branch: member.branch || '',
    joiningDate: member.joiningDate,
    createdAt: member.joiningDate || member.createdAt || '',
    emergencyContact: member.emergencyContact || '',
    status: member.status || 'active',
  };
}

export function formToMemberRecord(form) {
  return {
    ...form,
    name: buildMemberName(form),
    phone: form.mobile,
    address: buildMemberAddress(form),
  };
}

export function calculateAge(dob) {
  if (!dob) return '—';
  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) return '—';
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age -= 1;
  }
  return age;
}

/** True when member has no package serial yet (placeholder / old GYM- ids). */
export function isPendingGymMemberId(value) {
  const raw = String(value || '').trim();
  if (!raw || raw === '—') return true;
  if (/^PENDING-/i.test(raw)) return true;
  if (/^TMP-/i.test(raw)) return true;
  // Legacy createMember allotment — not a real membership serial
  if (/^GYM-\d+$/i.test(raw)) return true;
  return false;
}

/**
 * Public numeric serial from the member record (members.gym_member_id).
 * Prefer this over memberships.serial_no so Members / Memberships stay aligned.
 */
export function getMemberPublicSerial(member) {
  if (!member) return null;
  const candidates = [
    member.gym_member_id,
    member.baseGymId,
    // Only trust gymId when it is a plain serial / ID-serial (not GYM-xxxx)
    member.gymId,
  ];
  for (const value of candidates) {
    const raw = String(value || '').trim();
    if (!raw || isPendingGymMemberId(raw)) continue;
    const stripped = raw.replace(/^id[\s\-:_]*/i, '');
    if (/^\d+$/.test(stripped)) return Number(stripped);
  }
  return null;
}

export function getMembershipSerialNo(record) {
  if (!record) return null;
  const value = record.serialNo ?? record.membershipSerialNo ?? record.membership_serial_no ?? record.serial_no;
  if (value == null || value === '') return null;
  // Strip optional "ID" / "ID-" prefix from display values
  const stripped = String(value).trim().replace(/^id[\s\-_:]*/i, '');
  if (!stripped || stripped === '—' || isPendingGymMemberId(stripped)) return null;
  const num = Number(stripped);
  if (Number.isFinite(num)) return num;
  return stripped || null;
}

/** Display format: ID-42 (serial with ID prefix). */
export function formatSerialDisplay(serialNo) {
  if (serialNo == null || serialNo === '') return '';
  const raw = String(serialNo).trim();
  if (!raw || raw === '—' || isPendingGymMemberId(raw)) return '';
  if (/^id[\s\-:_]/i.test(raw)) {
    const rest = raw.replace(/^id[\s\-:_]*/i, '').trim();
    return rest ? `ID-${rest}` : '';
  }
  return `ID-${raw}`;
}

/**
 * Internal DB Gym ID (GYM-0001). Not shown in UI — use getGymMemberId for display.
 * Strips a trailing "-{serial}" if a combined value was stored by mistake.
 */
export function getBaseGymMemberId(memberOrId) {
  if (memberOrId == null || memberOrId === '') return '';
  if (typeof memberOrId === 'object') {
    const serial = getMembershipSerialNo(memberOrId);
    const raw = String(
      memberOrId.baseGymId
      || memberOrId.gym_member_id
      || (
        // Prefer raw DB id; ignore display gymId when it is only a serial / ID-serial
        (memberOrId.gymId != null
          && !/^\d+$/.test(String(memberOrId.gymId).trim())
          && !/^id[\s\-:_]*\d+/i.test(String(memberOrId.gymId).trim())
          ? memberOrId.gymId
          : '')
      )
      || '',
    ).trim();
    if (!raw) return '';
    if (serial != null && raw.endsWith(`-${serial}`)) {
      return raw.slice(0, -(String(serial).length + 1));
    }
    return stripGymIdSerialSuffix(raw);
  }
  const text = String(memberOrId).trim();
  if (/^\d+$/.test(text) || /^id[\s\-:_]*\d+/i.test(text)) return '';
  return stripGymIdSerialSuffix(text);
}

/** GYM-0001-42 → GYM-0001; leaves plain GYM-0001 unchanged. */
export function stripGymIdSerialSuffix(gymId) {
  const raw = String(gymId || '').trim();
  if (!raw) return '';
  const gymStyle = raw.match(/^(GYM-\d+)(?:-\d+)?$/i);
  if (gymStyle) return gymStyle[1];
  return raw;
}

/** @deprecated Prefer getGymMemberId — UI shows ID-{serial}. */
export function formatGymIdWithSerial(_gymId, serialNo) {
  return formatSerialDisplay(serialNo);
}

/**
 * Public member ID shown in receptionist/admin UI: ID-{serial} (e.g. ID-42).
 * Falls back to numeric gym_member_id when serial is not on the record yet.
 */
export function getGymMemberId(member) {
  if (!member) return '';
  const serial = getMembershipSerialNo(member);
  if (serial != null) return formatSerialDisplay(serial);
  const rawId = String(member.gym_member_id || member.baseGymId || member.gymId || '').trim();
  if (!rawId || isPendingGymMemberId(rawId)) return '';
  if (/^\d+$/.test(rawId)) return formatSerialDisplay(rawId);
  if (/^id[\s\-:_]*\d+/i.test(rawId)) return formatSerialDisplay(rawId);
  return '';
}

/** Numeric key for sorting by serial ascending (missing serials last). */
export function serialSortKey(record) {
  if (!record) return Number.POSITIVE_INFINITY;
  const serial = getMembershipSerialNo(record);
  if (serial != null && serial !== '') {
    const n = Number(String(serial).replace(/\D/g, ''));
    if (Number.isFinite(n)) return n;
  }
  const raw = String(
    record.gym_member_id || record.baseGymId || record.gymId || '',
  ).trim();
  if (!raw || isPendingGymMemberId(raw)) return Number.POSITIVE_INFINITY;
  const digits = raw.replace(/\D/g, '');
  if (digits) {
    const n = Number(digits);
    if (Number.isFinite(n)) return n;
  }
  return Number.POSITIVE_INFINITY;
}

/** Sort members / memberships by serial number ascending. */
export function sortBySerialAsc(list) {
  return [...(list || [])].sort((a, b) => {
    const diff = serialSortKey(a) - serialSortKey(b);
    if (diff !== 0) return diff;
    const nameA = String(a.name || a.member || a.memberName || '');
    const nameB = String(b.name || b.member || b.memberName || '');
    return nameA.localeCompare(nameB);
  });
}

export function gymMemberIdsMatch(a, b) {
  const leftSerial = getMembershipSerialNo(typeof a === 'object' ? a : { serialNo: a });
  const rightSerial = getMembershipSerialNo(typeof b === 'object' ? b : { serialNo: b });
  if (leftSerial != null && rightSerial != null && String(leftSerial) === String(rightSerial)) {
    return true;
  }
  const left = String(getBaseGymMemberId(a) || '').toLowerCase().replace(/\s+/g, '');
  const right = String(getBaseGymMemberId(b) || '').toLowerCase().replace(/\s+/g, '');
  return Boolean(left && right && left === right);
}

export function getGymIdByMemberName(name) {
  if (!name || name === '—' || name === 'Walk-In Customer') return '';
  return '';
}

export function getMemberCreatedAt(member) {
  const value = member?.joiningDate || member?.createdAt || '';
  if (!value || !String(value).trim()) return '—';
  return String(value).slice(0, 10);
}
